import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { io } from 'socket.io-client'
import axios from 'axios'
import { useAuthStore } from './authStore.js'

/**
 * Device Store - Manages device shadows and real-time updates
 * Implements the frontends side of the Device Shadow pattern
 */
export const useDeviceStore = defineStore('devices', () => {
  // State
  const devices = ref(new Map())
  const connectionStatus = ref('disconnected')
  const socket = ref(null)
  const isInitialized = ref(false)
  const dashboardStats = ref({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    recentActivity: 0,
    uptimePercentage: 0
  })
  const isLoading = ref(false)
  const error = ref(null)

  // Computed
  const deviceList = computed(() => Array.from(devices.value.values()))
  const onlineDevices = computed(() => deviceList.value.filter(device => device.status === 'online'))
  const offlineDevices = computed(() => deviceList.value.filter(device => device.status === 'offline'))
  const isConnected = computed(() => connectionStatus.value === 'connected')

  /**
   * Initialize WebSocket connection
   */
  function initializeSocket() {
    const serverUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
    const authStore = useAuthStore()
    
    socket.value = io(serverUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: {
        token: authStore.getSocketAuthToken()
      }
    })

    // Connection events
    socket.value.on('connect', () => {
      connectionStatus.value = 'connected'
      console.log('✅ Connected to IoT platform')
      
      // Subscribe to all device updates
      socket.value.emit('subscribe_devices')
    })

    socket.value.on('disconnect', (reason) => {
      connectionStatus.value = 'disconnected'
      console.log(`❌ Disconnected from IoT platform: ${reason}`)
    })

    socket.value.on('connect_error', (error) => {
      connectionStatus.value = 'error'
      console.error('🔌 Connection error:', error)
    })

    socket.value.on('reconnect', (attemptNumber) => {
      connectionStatus.value = 'connected'
      console.log(`🔄 Reconnected to IoT platform (attempt ${attemptNumber})`)
    })

    socket.value.on('reconnecting', (attemptNumber) => {
      connectionStatus.value = 'reconnecting'
      console.log(`🔄 Reconnecting to IoT platform (attempt ${attemptNumber})`)
    })

    // Device update events
    socket.value.on('device_updated', handleDeviceUpdate)
    socket.value.on('device_status_changed', handleStatusChange)
    socket.value.on('device_deleted', handleDeviceDeleted)
    socket.value.on('dashboard_stats_updated', handleDashboardStatsUpdate)
    socket.value.on('system_notification', handleSystemNotification)

    // Confirmation events
    socket.value.on('connection_confirmed', (data) => {
      console.log('🤝 Connection confirmed:', data.socketId)
    })

    socket.value.on('pong', () => {
      // Handle ping/pong for connection health monitoring
    })
  }

  /**
   * Handle device shadow updates from backend
   */
  function handleDeviceUpdate(payload) {
    const { deviceId, data } = payload
    devices.value.set(deviceId, data)
    console.log(`📱 Device updated: ${deviceId}`)
  }

  /**
   * Handle device status changes (online/offline)
   */
  function handleStatusChange(payload) {
    const { deviceId, status } = payload
    const device = devices.value.get(deviceId)
    
    if (device) {
      device.status = status
      device.lastSeen = new Date().toISOString()
      devices.value.set(deviceId, device)
      console.log(`📊 Device ${deviceId} status: ${status}`)
    }
  }

  /**
   * Handle device deletion from backend
   */
  function handleDeviceDeleted(payload) {
    const { deviceId } = payload
    devices.value.delete(deviceId)
    console.log(`🗑️ Device deleted: ${deviceId}`)
  }

  /**
   * Handle dashboard stats updates from backend
   */
  function handleDashboardStatsUpdate(payload) {
    dashboardStats.value = payload.data
    console.log('📊 Dashboard stats updated:', payload.data)
  }

  /**
   * Handle system notifications
   */
  function handleSystemNotification(notification) {
    console.log('🔔 System notification:', notification)
    // Could emit to a notification system here
  }

  /**
   * Check if we should make API calls (prevents calls when auth is required but not provided)
   */
  function shouldMakeApiCalls() {
    const authStore = useAuthStore()
    // Only make API calls if auth is disabled OR we are authenticated
    return !authStore.needsAuth
  }

  /**
   * Fetch all devices from API
   */
  async function fetchDevices() {
    if (!shouldMakeApiCalls()) {
      console.log('🚫 Skipping API call - authentication required')
      return
    }
    
    try {
      isLoading.value = true
      error.value = null
      
      const response = await axios.get('/api/devices')
      
      if (response.data.success) {
        // Convert array to Map for efficient lookups
        const deviceMap = new Map()
        response.data.data.devices.forEach(device => {
          deviceMap.set(device.deviceId, device)
        })
        devices.value = deviceMap
        
        console.log(`📋 Loaded ${deviceMap.size} devices`)
      }
    } catch (err) {
      error.value = err.response?.data?.error || err.message
      console.error('❌ Failed to fetch devices:', error.value)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get specific device by ID
   */
  async function getDevice(deviceId) {
    if (!shouldMakeApiCalls()) {
      console.log('🚫 Skipping API call - authentication required')
      return
    }
    
    try {
      const response = await axios.get(`/api/devices/${deviceId}`)
      
      if (response.data.success) {
        const device = response.data.data
        devices.value.set(deviceId, device)
        return device
      }
    } catch (err) {
      console.error(`❌ Failed to fetch device ${deviceId}:`, err)
      throw err
    }
  }

  /**
   * Update device desired state
   */
  async function updateDesiredState(deviceId, desiredState) {
    if (!shouldMakeApiCalls()) {
      console.log('🚫 Skipping API call - authentication required')
      return
    }
    
    try {
      const response = await axios.put(`/api/devices/${deviceId}/desired`, {
        desiredState
      })
      
      if (response.data.success) {
        const updatedDevice = response.data.data
        devices.value.set(deviceId, updatedDevice)
        console.log(`✅ Updated desired state for ${deviceId}`)
        return updatedDevice
      }
    } catch (err) {
      error.value = err.response?.data?.error || err.message
      console.error(`❌ Failed to update desired state for ${deviceId}:`, error.value)
      throw err
    }
  }

  /**
   * Trigger device sync
   */
  async function triggerDeviceSync(deviceId) {
    try {
      const response = await axios.post(`/api/devices/${deviceId}/sync`)
      
      if (response.data.success) {
        console.log(`🔄 Triggered sync for device ${deviceId}`)
      }
    } catch (err) {
      console.error(`❌ Failed to trigger sync for ${deviceId}:`, err)
      throw err
    }
  }

  /**
   * Subscribe to specific device updates
   */
  function subscribeToDevice(deviceId) {
    if (socket.value) {
      socket.value.emit('subscribe_device', deviceId)
      console.log(`👀 Subscribed to device: ${deviceId}`)
    }
  }

  /**
   * Unsubscribe from specific device updates
   */
  function unsubscribeFromDevice(deviceId) {
    if (socket.value) {
      socket.value.emit('unsubscribe_device', deviceId)
      console.log(`👋 Unsubscribed from device: ${deviceId}`)
    }
  }

  /**
   * Send ping to check connection health
   */
  function ping() {
    if (socket.value) {
      socket.value.emit('ping')
    }
  }

  /**
   * Disconnect WebSocket
   */
  function disconnect() {
    if (socket.value) {
      socket.value.disconnect()
      connectionStatus.value = 'disconnected'
    }
    isInitialized.value = false
  }

  /**
   * Clear all data (for logout/reset)
   */
  function clearStore() {
    devices.value.clear()
    dashboardStats.value = {
      totalDevices: 0,
      onlineDevices: 0,
      offlineDevices: 0,
      recentActivity: 0,
      uptimePercentage: 0
    }
    error.value = null
    isLoading.value = false
    isInitialized.value = false
  }

  /**
   * Delete a device by ID (API + local)
   */
  async function deleteDevice(deviceId) {
    try {
      isLoading.value = true
      error.value = null
      const response = await axios.delete(`/api/devices/${deviceId}`)
      if (response.data.success) {
        devices.value.delete(deviceId)
        console.log(`🗑️ Deleted device ${deviceId}`)
      } else {
        throw new Error(response.data.error || 'Delete failed')
      }
    } catch (err) {
      error.value = err.response?.data?.error || err.message
      console.error(`❌ Failed to delete device ${deviceId}:`, error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    devices,
    connectionStatus,
    dashboardStats,
    isLoading,
    error,
    isInitialized,
    
    // Computed
    deviceList,
    onlineDevices,
    offlineDevices,
    isConnected,
    
    // Actions
    initializeSocket,
    fetchDevices,
    getDevice,
    updateDesiredState,
    triggerDeviceSync,
    subscribeToDevice,
    unsubscribeFromDevice,
    ping,
    disconnect,
    clearStore,
    deleteDevice
  }
})